import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import type { InvoiceItemPresetResponse } from '@/shared/api';
import { SortablePresetRow } from './SortablePresetRow';

interface Props {
  presets: InvoiceItemPresetResponse[];
  onReorder: (next: InvoiceItemPresetResponse[]) => void;
  onEdit: (preset: InvoiceItemPresetResponse) => void;
  onDelete: (preset: InvoiceItemPresetResponse) => void;
}

/**
 * Die sortierbare Liste des Positionskatalogs.
 *
 * <p>Der Tastatursensor ist der zweite Teil des Touch-Befunds aus C3: ohne ihn bleibt Sortieren
 * eine reine Mausfunktion, egal wie sichtbar der Griff ist.</p>
 */
export function PresetSortableList({ presets, onReorder, onEdit, onDelete }: Props) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return;
    const from = presets.findIndex((preset) => preset.id === active.id);
    const to = presets.findIndex((preset) => preset.id === over.id);
    if (from === -1 || to === -1) return;
    onReorder(arrayMove(presets, from, to));
  };

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext
        items={presets.map((preset) => preset.id)}
        strategy={verticalListSortingStrategy}
      >
        <ul className="border-line bg-card-alt/30 rounded-md border">
          {presets.map((preset) => (
            <SortablePresetRow
              key={preset.id}
              preset={preset}
              onEdit={() => onEdit(preset)}
              onDelete={() => onDelete(preset)}
            />
          ))}
        </ul>
      </SortableContext>
    </DndContext>
  );
}
